<script lang="ts" setup>
import { Swiper, SwiperSlide } from 'swiper/vue'
import { Pagination } from 'swiper/modules'
import splitIntoChunks from '~/utils/splitIntoChunks'
import getWidgetTranslatedMediaUrl from '~/utils/getWidgetTranslatedMediaUrl'
import type { TranslatableString } from '~/types/widgets/widgetTypes'

const { t } = useLang()

const props = defineProps<{
  storeList?: { name: string; url: string; vpnOnly: boolean }[]
  storeLogos?: { logo: TranslatableString; url: string }[]
}>()

const showMoreStores = ref<boolean>(false)

const MAX_COLUMN_QTY = 4
const storeListColumns = computed(() => splitIntoChunks([...props.storeList], MAX_COLUMN_QTY))
const isValidUrl = (url: string) => {
  try {
    return Boolean(new URL(url))
  } catch (e) {
    return false
  }
}

const toggleShowMoreStores = (): void => {
  showMoreStores.value = !showMoreStores.value
}

watch(
  () => props.storeList,
  () => {
    showMoreStores.value = false
  }
)
</script>

<template>
  <div class="tab-content">
    <div v-if="Boolean(props.storeLogos?.length)" class="flex flex-col">
      <span class="text-lg font-medium mb-4 text-text1 dark:text-dmText1">{{
        t('quotes.popular-stores-in-category')
      }}</span>
      <div class="flex flex-col xl:flex-row gap-4">
        <div class="overflow-x-auto xl:max-w-[72rem]">
          <Swiper
            slides-per-view="auto"
            :space-between="30"
            :free-mode="true"
            :modules="[Pagination]"
            :pagination="{ clickable: true, dynamicBullets: true }"
            :threshold="5">
            <SwiperSlide v-for="(logo, index) in props.storeLogos" :key="index">
              <a v-if="isValidUrl(logo.url)" :href="logo.url" target="_blank" class="logo flex p-4">
                <img
                  v-if="getWidgetTranslatedMediaUrl(logo.logo)"
                  :src="getWidgetTranslatedMediaUrl(logo.logo)"
                  height="50px" />
              </a>
            </SwiperSlide>
          </Swiper>
        </div>
        <div v-if="props.storeList" class="flex items-center">
          <div class="w-full xl:max-w-[14rem] pb-6">
            <Button button-type="trinary" class="w-full" @click.prevent="toggleShowMoreStores">
              <span class="flex flex-row gap-2 text-sm">
                <span>{{ t('common.more_stores') }}</span>
                <span v-if="showMoreStores">
                  <i class="fa fa-chevron-up" />
                </span>
                <span v-else>
                  <i class="fa fa-chevron-down" />
                </span>
              </span>
            </Button>
          </div>
        </div>
      </div>
    </div>
    <hr v-if="Boolean(props.storeLogos?.length)" />
    <template v-if="showMoreStores">
      <span class="text-lg font-medium mb-4 text-text1 dark:text-dmText1">{{ t('common.all-stores') }}</span>
      <div class="flex w-full flex-wrap gap-x-3">
        <ul
          v-for="(column, index) in storeListColumns"
          :key="`column-${index}`"
          class="flex flex-col min-w-[7.5rem] max-w-[22.5rem] flex-1">
          <li
            v-for="(store, storeIndex) in column"
            :key="`store-${store.name}-${storeIndex}`"
            class="store-url text-text1 dark:text-dmText1 text-lg">
            <a v-if="isValidUrl(store.url)" :href="store.url" target="_blank">
              {{ store.name }}
            </a>
            <span v-if="store.vpnOnly" class="text-brand-primary ml-1">*</span>
          </li>
        </ul>
      </div>
    </template>
    <span class="vpn-disclaimer lg:mt-4 px-4 py-2 bg-white dark:bg-dmGray1 rounded-md">
      <i class="fa-solid fa-circle-question text-neutral3 dark:text-dmText3" />
      <span class="text-brand-primary mx-1">*</span>
      <span class="text-text1 dark:text-dmText1" v-html="t('quotes.vpn-disclaimer')" />
    </span>
  </div>
</template>

<style lang="postcss">
.tab-content {
  @apply flex w-full flex-col;
  .swiper {
    @apply w-full pt-2 pb-2 xl:pb-8;
  }
}
</style>

<style lang="postcss" scoped>
.tab-content {
  .swiper {
    padding-bottom: 2.5rem;

    .swiper-slide {
      width: min-content !important;
      display: flex;
    }
  }

  .store.vpn-only {
    &::after {
      content: '*';
      color: theme('colors.brand.primary');
      margin-left: 0.25rem;
    }
  }

  .vpn-disclaimer {
    width: fit-content;

    :deep(a) {
      color: theme('colors.brand.primary');
    }
  }

  .store-url {
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
  }

  .logo {
    background: theme('colors.white');
    border: theme('vars.borderLight');
    border-radius: theme('vars.borderRadiusLight');
    width: 10.375rem;
    height: 5rem;

    img {
      max-height: 3.125rem;
      height: 100%;
      margin: auto;
    }
  }
}
</style>
